import {
  collection, query, where, orderBy, DocumentData, addDoc, updateDoc, doc as fDoc,
  getDocs, getDoc
} from 'firebase/firestore';
import { db } from '../firebase';

// The template-variable registry is system/template data (names, labels,
// data paths, sample values) — NOT applicant PII. It must be read with
// plain Firestore getters, never through secureApplicationsRepo: that
// repo POSTs every document to /api/pii/decrypt anchored on a stored
// applications/{id} record, and a registry id has no such anchor, so the
// endpoint refuses every registry read with HTTP 403 (the 2026-10-26
// Variable Management / Template Editor outage).

export interface TemplateVariable {
  id: string;
  name: string;
  label: string;
  group: string;
  dataPath: string | null;
  sampleValue: string;
  deprecated?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Get all template variables from Firestore (including or excluding deprecated)
 */
export async function getTemplateVariables(includeDeprecated = false): Promise<TemplateVariable[]> {
  try {
    let q;
    if (includeDeprecated) {
      q = query(collection(db, 'templateVariables'));
    } else {
      q = query(
        collection(db, 'templateVariables'),
        where('deprecated', '==', false)
      );
    }

    const snapshot = await getDocs(q);
    const variables: TemplateVariable[] = [];

    snapshot.forEach((doc) => {
      const data = doc.data() as DocumentData;
      variables.push({
        id: doc.id,
        name: data.name,
        label: data.label,
        group: data.group,
        dataPath: data.dataPath || null,
        sampleValue: data.sampleValue || '',
        deprecated: data.deprecated || false,
        createdAt: data.createdAt,
        updatedAt: data.updatedAt,
      });
    });

    return variables;
  } catch (error) {
    console.error('Failed to fetch template variables:', error);
    return [];
  }
}

/**
 * Get a single template variable by ID
 */
export async function getTemplateVariable(id: string): Promise<TemplateVariable | null> {
  try {
    const docRef = fDoc(db, 'templateVariables', id);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    const data = docSnap.data() as DocumentData;
    return {
      id: docSnap.id,
      name: data.name,
      label: data.label,
      group: data.group,
      dataPath: data.dataPath || null,
      sampleValue: data.sampleValue || '',
      deprecated: data.deprecated || false,
      createdAt: data.createdAt,
      updatedAt: data.updatedAt,
    };
  } catch (error) {
    console.error('Failed to fetch template variable:', error);
    return null;
  }
}

/**
 * Create a new template variable
 */
export async function createTemplateVariable(
  variable: Omit<TemplateVariable, 'id' | 'createdAt' | 'updatedAt'>
): Promise<string> {
  try {
    const now = new Date().toISOString();
    const docRef = await addDoc(collection(db, 'templateVariables'), {
      ...variable,
      deprecated: variable.deprecated ?? false,
      createdAt: now,
      updatedAt: now,
    });

    return docRef.id;
  } catch (error) {
    console.error('Failed to create template variable:', error);
    throw error;
  }
}

/**
 * Update an existing template variable
 */
export async function updateTemplateVariable(
  id: string,
  variable: Partial<Omit<TemplateVariable, 'id' | 'createdAt'>>
): Promise<void> {
  try {
    const updateData = {
      ...variable,
      updatedAt: new Date().toISOString(),
    };

    const docRef = fDoc(db, 'templateVariables', id);
    await updateDoc(docRef, updateData);
  } catch (error) {
    console.error('Failed to update template variable:', error);
    throw error;
  }
}

/**
 * Deprecate a template variable (soft delete)
 */
export async function deprecateTemplateVariable(id: string): Promise<void> {
  try {
    const docRef = fDoc(db, 'templateVariables', id);
    await updateDoc(docRef, {
      deprecated: true,
      updatedAt: new Date().toISOString(),
    });
  } catch (error) {
    console.error('Failed to deprecate template variable:', error);
    throw error;
  }
}

/**
 * Get variables grouped by group for UI display
 */
export async function getGroupedTemplateVariables(
  includeDeprecated = false
): Promise<Array<{ label: string; variables: TemplateVariable[] }>> {
  try {
    const variables = await getTemplateVariables(includeDeprecated);

    const grouped = variables.reduce((acc: Map<string, TemplateVariable[]>, variable) => {
      if (!acc.has(variable.group)) {
        acc.set(variable.group, []);
      }
      acc.get(variable.group)!.push(variable);
      return acc;
    }, new Map<string, TemplateVariable[]>());

    return Array.from(grouped.entries())
      .map(([label, vars]) => ({
        label,
        variables: vars.sort((a, b) => a.label.localeCompare(b.label)),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  } catch (error) {
    console.error('Failed to group template variables:', error);
    return [];
  }
}

// Re-export lodash get equivalent for resolving dot paths
export function get(obj: any, path: string | null | undefined): any {
  if (!path || typeof obj !== 'object' || obj === null) {
    return undefined;
  }

  const segments = path.split('.');
  let current = obj;

  for (const segment of segments) {
    if (current == null || !(segment in current)) {
      return undefined;
    }
    current = current[segment];
  }

  return current;
}
